import { apiClient } from './apiClient';

type LaneVerdict = 'healthy' | 'degraded' | 'stalled' | 'unknown';
type Readiness = 'ready' | 'not_ready' | 'unknown';

interface BackendOverview {
  db_now: string;
  total_rows: number;
  latest_imported_at: string | null;
  imports_last_1h: number;
  imports_last_6h: number;
  imports_last_24h: number;
  rows_with_vetting_fields: number;
  run_ids_last_24h: number;
  tenants_last_24h: number;
  keywords_last_24h: number;
  verdict: string;
  verdict_reason: string;
}

interface BackendPoolRow {
  scope: 'tenant' | 'keyword';
  tenant_id: string;
  keyword: string | null;
  total_rows: number;
  distinct_keywords: number;
  distinct_run_ids: number;
  usable_rows: number;
  usable_keywords: number;
  accepted_rows: number;
  latest_imported_at: string | null;
  readiness: string;
}

interface BackendPoolsResponse {
  scope: 'tenant' | 'keyword';
  tenant_id: string | null;
  rows: BackendPoolRow[];
}

interface BackendImportGroup {
  imported_at: string | null;
  tenant_id: string;
  keyword: string;
  run_id: string;
  total_rows: number;
  usable_rows: number;
  accepted_rows: number;
  imported_from_sqlite_path: string | null;
}

interface BackendImportsResponse {
  rows: BackendImportGroup[];
}

interface BackendReadiness {
  tenant_id: string;
  keyword: string;
  safe_for_agent_sdk: boolean;
  usable_rows: number;
  total_rows: number;
  distinct_run_ids: number;
  latest_imported_at: string | null;
  predicate: string;
}

interface BackendExpectedJob {
  name: string;
  cron_id: string;
  expected_interval_minutes: number;
  description: string;
}

interface BackendLaneStatus {
  cron_known: boolean;
  source: string;
  worker_active: boolean | null;
  cron_scheduled: boolean | null;
  reported_at: string | null;
  expected_jobs: BackendExpectedJob[];
  message: string;
}

export interface ResearchCoverageOverview {
  lane_verdict: LaneVerdict;
  last_db_import: string | null;
  counts_1h: number;
  counts_6h: number;
  counts_24h: number;
  cron_status: 'running' | 'missing' | 'unknown';
  cron_last_heartbeat: string | null;
  total_rows: number;
  verdict_reason: string;
}

export interface ResearchPool {
  tenant_id: string;
  tenant_name: string;
  keyword: string;
  total_rows: number;
  usable_rows: number;
  distinct_runs: number;
  last_import_at: string | null;
  readiness: Readiness;
  usable_keywords: number;
  distinct_keywords: number;
}

export interface ResearchPoolsResponse {
  tenant_id?: string;
  pools: ResearchPool[];
}

export interface ResearchImport {
  id: string;
  timestamp: string | null;
  tenant_id: string;
  tenant_name: string;
  keyword: string;
  rows: number;
  usable_count: number;
  run_id: string | null;
  imported_from_sqlite_path: string | null;
}

export interface ResearchImportsResponse {
  imports: ResearchImport[];
}

export interface ResearchReadiness {
  tenant_id: string;
  keyword: string;
  total_rows: number;
  usable_rows: number;
  distinct_runs: number;
  readiness: Readiness;
  last_import_at: string | null;
  predicate: string;
}

export interface ResearchLaneStatus {
  cron_registry_state: 'registered' | 'missing' | 'unknown';
  expected_jobs: string[];
  worker_status: 'active' | 'idle' | 'unknown' | 'missing';
  worker_last_heartbeat: string | null;
  warnings: string[];
  message: string;
}

export type ResearchCandidatePolicy = 'approved_only' | 'allow_candidate';

const RESEARCH_JOB_STATUSES = [
  'queued',
  'running',
  'succeeded',
  'partial',
  'failed',
  'cancelled',
  'cancel_requested',
] as const;

const RESEARCH_KEYWORD_STATUSES = [
  'pending',
  'running',
  'done',
  'failed',
  'skipped',
] as const;

export type ResearchKnownJobStatus = (typeof RESEARCH_JOB_STATUSES)[number];
export type ResearchJobStatus = ResearchKnownJobStatus | 'unknown';
export type ResearchCoverageKeywordStatus = (typeof RESEARCH_KEYWORD_STATUSES)[number] | 'unknown';

export const ACTIVE_RESEARCH_JOB_STATUSES: ResearchKnownJobStatus[] = [
  'queued',
  'running',
  'cancel_requested',
];

export interface ResearchCoverageWarning {
  keyword: string;
  reason: string;
}

export interface ResearchCoverageTriggerJobResponse {
  job_id: string;
  tenant_id: string;
  status: ResearchJobStatus;
  keywords: string[];
  warnings: ResearchCoverageWarning[];
  created_at: string;
  created_by: string;
}

export type ResearchCoverageJobResponse = ResearchCoverageTriggerJobResponse;

export interface ResearchTriggerSelect {
  status: string[];
  limit_per_client?: number;
}

export interface ResearchTriggerJobRequest {
  tenant_id: string;
  keywords?: string[];
  feed_item_ids?: string[];
  select?: ResearchTriggerSelect;
  target_sources?: number;
  max_rounds?: number;
  candidate_policy?: ResearchCandidatePolicy;
  dry_run?: boolean;
}

export interface ResearchCoverageJobKeyword {
  keyword: string;
  status: ResearchCoverageKeywordStatus;
  run_id: string | null;
  qualified: number | null;
  rows_upserted: number;
  usable_rows: number;
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
}

export interface ResearchCoverageJobSummary {
  job_id: string;
  tenant_id: string;
  status: ResearchJobStatus;
  keywords_total: number;
  keywords_done: number;
  keywords_failed: number;
  rows_upserted: number;
  usable_rows: number;
  created_at: string | null;
  started_at: string | null;
  finished_at: string | null;
  created_by: string | null;
  keywords?: ResearchCoverageJobKeyword[];
}

export interface ResearchCoverageJobDetail extends ResearchCoverageJobSummary {
  keywords: ResearchCoverageJobKeyword[];
}

export interface ResearchCoverageJobsResponse {
  jobs: ResearchCoverageJobSummary[];
}

export interface ResearchCoverageJobListParams {
  tenant_id: string;
  status?: ResearchKnownJobStatus | ResearchKnownJobStatus[];
  limit?: number;
}

interface ResearchBackendErrorDetail {
  message?: string;
  warnings?: ResearchCoverageWarning[];
  [key: string]: unknown;
}

function createResearchIdempotencyKey() {
  if (globalThis.crypto?.randomUUID) {
    return `research-${globalThis.crypto.randomUUID()}`;
  }
  return `research-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function isActiveResearchCoverageJobStatus(status: string): status is ResearchKnownJobStatus {
  return ACTIVE_RESEARCH_JOB_STATUSES.includes(status as ResearchKnownJobStatus);
}

export function getResearchCoverageErrorMessage(error: unknown): string {
  const err = error as {
    message?: string;
    response?: { status?: number; data?: { detail?: unknown } };
  };
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object') {
    const structured = detail as ResearchBackendErrorDetail;
    const warningText = structured.warnings?.length
      ? ` (${structured.warnings.length} warning${structured.warnings.length === 1 ? '' : 's'})`
      : '';
    if (typeof structured.message === 'string') return `${structured.message}${warningText}`;
    return JSON.stringify(detail);
  }
  if (err.response?.status === 409)
    return 'A research request is already running for this client. Wait for it to finish before starting another.';
  if (err.response?.status === 422)
    return 'That research request is missing some details. Check the form and try again.';
  if (err.response?.status === 503)
    return 'Finding sources is temporarily unavailable. Try again later or contact support.';
  return err.message ?? 'Research request failed.';
}

function mapLaneVerdict(verdict: string): LaneVerdict {
  if (verdict === 'healthy' || verdict === 'worker_active') return 'healthy';
  if (verdict.includes('stale') || verdict.includes('down')) return 'stalled';
  if (verdict.includes('fresh') || verdict.includes('recent') || verdict.includes('missing')) {
    return 'degraded';
  }
  return 'unknown';
}

function mapReadiness(value: string | boolean): Readiness {
  if (value === true || value === 'safe' || value === 'ready') return 'ready';
  if (value === false || value === 'missing_usable_pool' || value === 'not_ready') {
    return 'not_ready';
  }
  return 'unknown';
}

function mapPool(row: BackendPoolRow): ResearchPool {
  return {
    tenant_id: row.tenant_id,
    tenant_name: row.tenant_id,
    keyword:
      row.scope === 'tenant'
        ? `${row.distinct_keywords} keyword${row.distinct_keywords === 1 ? '' : 's'}`
        : row.keyword ?? 'Unknown keyword',
    total_rows: row.total_rows,
    usable_rows: row.usable_rows,
    distinct_runs: row.distinct_run_ids,
    last_import_at: row.latest_imported_at,
    readiness: mapReadiness(row.readiness),
    usable_keywords: row.usable_keywords,
    distinct_keywords: row.distinct_keywords,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringValue(value: unknown, fallback = ''): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return fallback;
}

function nullableString(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return null;
}

function numberValue(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

function normalizeJobStatus(value: unknown): ResearchJobStatus {
  const status = stringValue(value);
  return RESEARCH_JOB_STATUSES.includes(status as ResearchKnownJobStatus)
    ? (status as ResearchKnownJobStatus)
    : 'unknown';
}

function normalizeKeywordStatus(value: unknown): ResearchCoverageKeywordStatus {
  const status = stringValue(value);
  return RESEARCH_KEYWORD_STATUSES.includes(status as Exclude<ResearchCoverageKeywordStatus, 'unknown'>)
    ? (status as Exclude<ResearchCoverageKeywordStatus, 'unknown'>)
    : 'unknown';
}

function normalizeStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .map((item) => stringValue(item).trim())
      .filter(Boolean);
  }
  if (typeof value === 'string' && value.trim()) {
    try {
      return normalizeStringArray(JSON.parse(value));
    } catch {
      return value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
    }
  }
  return [];
}

function normalizeWarnings(value: unknown): ResearchCoverageWarning[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((warning) => {
      if (!isRecord(warning)) return null;
      return {
        keyword: stringValue(warning.keyword),
        reason: stringValue(warning.reason),
      };
    })
    .filter((warning): warning is ResearchCoverageWarning => warning !== null);
}

function normalizeJobKeyword(value: unknown): ResearchCoverageJobKeyword {
  if (typeof value === 'string') {
    return {
      keyword: value,
      status: 'unknown',
      run_id: null,
      qualified: null,
      rows_upserted: 0,
      usable_rows: 0,
      error: null,
      started_at: null,
      finished_at: null,
    };
  }

  const row = isRecord(value) ? value : {};
  return {
    keyword: stringValue(row.keyword),
    status: normalizeKeywordStatus(row.status),
    run_id: nullableString(row.run_id),
    qualified: row.qualified === null || row.qualified === undefined ? null : numberValue(row.qualified),
    rows_upserted: numberValue(row.rows_upserted),
    usable_rows: numberValue(row.usable_rows),
    error: nullableString(row.error),
    started_at: nullableString(row.started_at),
    finished_at: nullableString(row.finished_at),
  };
}

function normalizeJobKeywords(value: unknown): ResearchCoverageJobKeyword[] {
  if (!Array.isArray(value)) return [];
  return value.map(normalizeJobKeyword).filter((keyword) => keyword.keyword);
}

function normalizeJobSummary(value: unknown): ResearchCoverageJobSummary {
  const row = isRecord(value) ? value : {};
  const keywords = normalizeJobKeywords(row.keywords);
  const summary: ResearchCoverageJobSummary = {
    job_id: stringValue(row.job_id ?? row.id),
    tenant_id: stringValue(row.tenant_id),
    status: normalizeJobStatus(row.status),
    keywords_total: numberValue(row.keywords_total) || keywords.length,
    keywords_done: numberValue(row.keywords_done),
    keywords_failed: numberValue(row.keywords_failed),
    rows_upserted: numberValue(row.rows_upserted),
    usable_rows: numberValue(row.usable_rows),
    created_at: nullableString(row.created_at),
    started_at: nullableString(row.started_at),
    finished_at: nullableString(row.finished_at),
    created_by: nullableString(row.created_by),
  };

  if (keywords.length > 0) {
    summary.keywords = keywords;
  }

  return summary;
}

function normalizeJobDetail(data: unknown): ResearchCoverageJobDetail {
  const row = isRecord(data) ? data : {};
  return {
    ...normalizeJobSummary(row),
    keywords: normalizeJobKeywords(row.keywords),
  };
}

function normalizeTriggerJobResponse(data: unknown): ResearchCoverageTriggerJobResponse {
  const row = isRecord(data) ? data : {};
  return {
    job_id: stringValue(row.job_id ?? row.id),
    tenant_id: stringValue(row.tenant_id),
    status: normalizeJobStatus(row.status),
    keywords: normalizeStringArray(row.keywords),
    warnings: normalizeWarnings(row.warnings),
    created_at: nullableString(row.created_at) ?? '',
    created_by: nullableString(row.created_by) ?? '',
  };
}

function extractJobRows(data: unknown): unknown[] {
  if (Array.isArray(data)) {
    return data;
  }
  if (!isRecord(data)) return [];

  for (const key of ['jobs', 'rows', 'items']) {
    const value = data[key];
    if (Array.isArray(value)) return value;
  }

  return extractJobRows(data.data);
}

function normalizeJobsResponse(data: unknown): ResearchCoverageJobsResponse {
  return {
    jobs: extractJobRows(data).map(normalizeJobSummary),
  };
}

function buildJobListQueryParams(
  params: ResearchCoverageJobListParams,
  statusOverride?: ResearchKnownJobStatus,
) {
  return {
    tenant_id: params?.tenant_id,
    status: statusOverride ?? (Array.isArray(params?.status) ? undefined : params?.status),
    limit: params?.limit,
  };
}

function sortJobsNewestFirst(jobs: ResearchCoverageJobSummary[]) {
  return [...jobs].sort((left, right) => {
    const leftTime = left.created_at ? new Date(left.created_at).getTime() : 0;
    const rightTime = right.created_at ? new Date(right.created_at).getTime() : 0;
    return rightTime - leftTime;
  });
}

async function fetchJobSummaries(
  params: ResearchCoverageJobListParams,
  statusOverride?: ResearchKnownJobStatus,
): Promise<ResearchCoverageJobSummary[]> {
  const { data } = await apiClient.get<unknown>('/hermes-vetting/jobs', {
    params: buildJobListQueryParams(params, statusOverride),
  });
  return normalizeJobsResponse(data).jobs;
}

export const researchCoverageService = {
  async triggerJob(
    request: ResearchTriggerJobRequest,
    idempotencyKey?: string,
  ): Promise<ResearchCoverageTriggerJobResponse> {
    const { data } = await apiClient.post<unknown>(
      '/hermes-vetting/jobs',
      request,
      {
        headers: { 'Idempotency-Key': idempotencyKey?.trim() || createResearchIdempotencyKey() },
      },
    );
    return normalizeTriggerJobResponse(data);
  },

  async listJobs(params: ResearchCoverageJobListParams): Promise<ResearchCoverageJobsResponse> {
    if (!Array.isArray(params?.status)) {
      return { jobs: await fetchJobSummaries(params) };
    }

    const statuses = Array.from(new Set(params.status));
    const jobsById = new Map<string, ResearchCoverageJobSummary>();
    const results = await Promise.all(
      statuses.map((status) => fetchJobSummaries(params, status)),
    );
    for (const job of results.flat()) {
      jobsById.set(job.job_id, job);
    }
    const jobs = sortJobsNewestFirst(Array.from(jobsById.values()));
    return { jobs: typeof params.limit === 'number' ? jobs.slice(0, params.limit) : jobs };
  },

  async getJob(jobId: string, tenantId: string): Promise<ResearchCoverageJobDetail> {
    const { data } = await apiClient.get<unknown>(
      `/hermes-vetting/jobs/${jobId}`,
      { params: { tenant_id: tenantId } },
    );
    return normalizeJobDetail(data);
  },

  async cancelJob(jobId: string, tenantId: string): Promise<ResearchCoverageJobDetail> {
    const { data } = await apiClient.post<unknown>(
      `/hermes-vetting/jobs/${jobId}/cancel`,
      null,
      { params: { tenant_id: tenantId } },
    );
    return normalizeJobDetail(data);
  },

  async getOverview(tenantId: string): Promise<ResearchCoverageOverview> {
    const { data } = await apiClient.get<BackendOverview>('/hermes-vetting/overview', {
      params: { tenant_id: tenantId },
    });
    return {
      lane_verdict: mapLaneVerdict(data.verdict),
      last_db_import: data.latest_imported_at,
      counts_1h: data.imports_last_1h,
      counts_6h: data.imports_last_6h,
      counts_24h: data.imports_last_24h,
      cron_status: 'unknown',
      cron_last_heartbeat: null,
      total_rows: data.total_rows,
      verdict_reason: data.verdict_reason,
    };
  },

  async getPools(tenantId: string): Promise<ResearchPoolsResponse> {
    const { data } = await apiClient.get<BackendPoolsResponse>('/hermes-vetting/pools', {
      params: { tenant_id: tenantId },
    });
    return {
      tenant_id: data.tenant_id ?? undefined,
      pools: data.rows.map(mapPool),
    };
  },

  async getImports(tenantId: string, limit = 50): Promise<ResearchImportsResponse> {
    const { data } = await apiClient.get<BackendImportsResponse>('/hermes-vetting/imports', {
      params: { tenant_id: tenantId, limit },
    });
    return {
      imports: data.rows.map((row) => ({
        id: `${row.run_id}:${row.tenant_id}:${row.keyword}`,
        timestamp: row.imported_at,
        tenant_id: row.tenant_id,
        tenant_name: row.tenant_id,
        keyword: row.keyword,
        rows: row.total_rows,
        usable_count: row.usable_rows,
        run_id: row.run_id,
        imported_from_sqlite_path: row.imported_from_sqlite_path,
      })),
    };
  },

  async getReadiness(tenantId: string, keyword: string): Promise<ResearchReadiness> {
    const { data } = await apiClient.get<BackendReadiness>('/hermes-vetting/readiness', {
      params: { tenant_id: tenantId, keyword },
    });
    return {
      tenant_id: data.tenant_id,
      keyword: data.keyword,
      total_rows: data.total_rows,
      usable_rows: data.usable_rows,
      distinct_runs: data.distinct_run_ids,
      readiness: mapReadiness(data.safe_for_agent_sdk),
      last_import_at: data.latest_imported_at,
      predicate: data.predicate,
    };
  },

  async getLaneStatus(): Promise<ResearchLaneStatus> {
    const { data } = await apiClient.get<BackendLaneStatus>('/hermes-vetting/lane-status');
    return {
      cron_registry_state: data.cron_known
        ? data.cron_scheduled
          ? 'registered'
          : 'missing'
        : 'unknown',
      expected_jobs: data.expected_jobs.map(
        (job) => `${job.name} (${job.expected_interval_minutes}m)`,
      ),
      worker_status: data.worker_active == null ? 'unknown' : data.worker_active ? 'active' : 'idle',
      worker_last_heartbeat: data.reported_at,
      warnings: data.cron_known ? [] : ['content-vps heartbeat is not configured yet'],
      message: data.message,
    };
  },
};
